import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, CalendarClock, Check, CheckCircle2, Coins, FileText, IndianRupee, Lightbulb, Quote, Scale, ScrollText, SearchCheck, ShieldCheck, Clock3, Sparkles, Truck } from "lucide-react";
import { api } from "@/api";
import { useLang } from "@/i18n";
import { useContact } from "@/components/ContactModal";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CalculatorCard } from "@/components/CalculatorCard";
import { BlogCard } from "@/components/BlogCard";
import { Seo } from "@/components/Seo";
import { scrollToId } from "@/utils/scroll";

const heroImage = "https://images.unsplash.com/photo-1557804506-669a67965ba0?crop=entropy&cs=srgb&fm=jpg&q=85";
const teamImage = "https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&q=85";
const serviceIcons = [ScrollText, FileText, SearchCheck, Scale, Truck, Lightbulb];
const floatIcons = [CheckCircle2, Coins, CalendarClock];

function Hero() {
  const { t } = useLang();
  const { open: openContact } = useContact();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const floats = t("hero.floats");

  return (
    <section className="hero section-wrap" ref={ref} data-testid="hero-section">
      <div className="hero-copy">
        <motion.span className="eyebrow hero-eyebrow" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Sparkles size={14} /> {t("hero.eyebrow")}
        </motion.span>
        <h1 className="hero-title">
          <MaskedLine delay={0.15}>{t("hero.l1")}</MaskedLine>
          <MaskedLine delay={0.3} className="hero-title-em">{t("hero.l2")}</MaskedLine>
        </h1>
        <motion.p className="hero-text" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55 }}>
          {t("hero.sub")}
        </motion.p>
        <motion.div className="hero-actions" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}>
          <button className="btn btn-dark" onClick={openContact} data-testid="hero-consultation-button">
            {t("hero.cta1")} <ArrowUpRight size={17} />
          </button>
          <button className="text-link" onClick={() => scrollToId("services")} data-testid="hero-services-link">
            {t("hero.cta2")} <span>↓</span>
          </button>
        </motion.div>
        <motion.div className="trust-row" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.9 }}>
          <span><ShieldCheck size={17} /> {t("hero.trust1")}</span>
          <span><Clock3 size={17} /> {t("hero.trust2")}</span>
        </motion.div>
      </div>
      <motion.div className="hero-visual" style={{ y: imgY }} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}>
        <div className="hero-frame">
          <img src={heroImage} alt={t("hero.imgAlt")} data-testid="hero-image" />
        </div>
        {floats.map((f, i) => {
          const Icon = floatIcons[i];
          return (
            <motion.div className={`float-card fc-${i + 1}`} key={f.t}
              animate={{ y: [0, -9, 0] }}
              transition={{ duration: 4.5 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.6 }}>
              <Icon size={17} />
              <span><b>{f.t}</b><small>{f.s}</small></span>
            </motion.div>
          );
        })}
        <div className="hero-stamp">
          {t("hero.stamp")}
          <small>{t("hero.stampSmall")}</small>
        </div>
      </motion.div>
    </section>
  );
}

function Statement() {
  const { t } = useLang();
  return (
    <section className="statement" data-testid="why-section">
      <div className="section-wrap statement-inner">
        <div>
          <Reveal><span className="eyebrow">{t("statement.eyebrow")}</span></Reveal>
          <Reveal delay={0.1}><h2>{t("statement.t1")}<br /><em>{t("statement.t2")}</em></h2></Reveal>
        </div>
        <Reveal delay={0.2}><p>{t("statement.body")}</p></Reveal>
      </div>
    </section>
  );
}

function Services() {
  const { t } = useLang();
  const { open: openContact } = useContact();
  const services = t("services");
  return (
    <section id="services" className="section-wrap services-section" data-testid="services-section">
      <div className="section-heading">
        <div>
          <Reveal><span className="eyebrow">{t("servicesSec.eyebrow")}</span></Reveal>
          <Reveal delay={0.08}><h2>{t("servicesSec.t1")}<br /><em>{t("servicesSec.t2")}</em></h2></Reveal>
        </div>
        <Reveal delay={0.15}><p>{t("servicesSec.sub")}</p></Reveal>
      </div>
      <div className="bento">
        {services.map((s, i) => {
          const Icon = serviceIcons[i];
          return (
            <Reveal key={s.id} delay={i * 0.06} className={i === 0 ? "bento-cell bento-feature" : "bento-cell"}>
              <article className="service-card" data-testid={`service-card-${s.id}`}>
                <div className="service-top">
                  <span className="service-num">0{i + 1}</span>
                  <Icon size={22} className="service-icon" />
                </div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
                <button onClick={openContact} data-testid={`service-enquire-${s.id}`}>
                  {t("servicesSec.enquire")} <ArrowUpRight size={16} />
                </button>
              </article>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function About() {
  const { t } = useLang();
  const { open: openContact } = useContact();
  return (
    <section className="split-section section-wrap" id="about" data-testid="about-section">
      <Reveal className="split-image">
        <img src={teamImage} alt={t("about.imgAlt")} data-testid="team-image" />
        <div className="caption">{t("about.cap1")}<br /><b>{t("about.cap2")}</b></div>
      </Reveal>
      <div className="split-copy">
        <Reveal><span className="eyebrow">{t("about.eyebrow")}</span></Reveal>
        <Reveal delay={0.08}><h2>{t("about.t1")}<br /><em>{t("about.t2")}</em></h2></Reveal>
        <Reveal delay={0.15}><p>{t("about.body")}</p></Reveal>
        <Reveal delay={0.2}>
          <div className="check-list">
            {t("about.checks").map((c) => <span key={c}><Check size={17} /> {c}</span>)}
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <button className="text-link" onClick={openContact} data-testid="about-contact-link">
            {t("about.cta")} <ArrowUpRight size={16} />
          </button>
        </Reveal>
      </div>
    </section>
  );
}

function Tools() {
  const { t } = useLang();
  return (
    <section className="tool-section" data-testid="tools-section">
      <div className="section-wrap tool-grid">
        <Reveal><CalculatorCard /></Reveal>
        <div className="tool-copy">
          <Reveal><span className="eyebrow">{t("tools.eyebrow")}</span></Reveal>
          <Reveal delay={0.08}><h2>{t("tools.t1")}<br /><em>{t("tools.t2")}</em></h2></Reveal>
          <Reveal delay={0.15}><p>{t("tools.body")}</p></Reveal>
          <Reveal delay={0.2}>
            <div className="tool-points">
              <div><FileText size={20} /><span><b>{t("tools.p1t")}</b><small>{t("tools.p1d")}</small></span></div>
              <div><IndianRupee size={20} /><span><b>{t("tools.p2t")}</b><small>{t("tools.p2d")}</small></span></div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function InsightsPreview() {
  const { t } = useLang();
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/blogs").then((r) => setPosts((r.data || []).slice(0, 3))).catch(() => {});
  }, []);
  return (
    <section id="insights" className="section-wrap insights-section" data-testid="insights-section">
      <div className="section-heading">
        <div>
          <Reveal><span className="eyebrow">{t("insights.eyebrow")}</span></Reveal>
          <Reveal delay={0.08}><h2>{t("insights.t1")}<br /><em>{t("insights.t2")}</em></h2></Reveal>
        </div>
        <Reveal delay={0.15}>
          <Link to="/blog" className="text-link" data-testid="view-all-blog-link">{t("insights.viewAll")} <ArrowUpRight size={16} /></Link>
        </Reveal>
      </div>
      <div className="blog-grid">
        {posts.map((post, i) => <BlogCard key={post.id} post={post} index={i} />)}
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  const { open: openContact } = useContact();
  return (
    <>
      <Seo title="RKJ GST Consultancy — GST Registration, Returns & Advisory" description="RKJ GST Consultancy helps Indian businesses with GST registration, return filing, audits, notices and practical tax advice. Book a consultation today." />
      <Hero />
      <Marquee />
      <Statement />
      <Services />
      <About />
      <Tools />
      <InsightsPreview />
      <section className="quote-section" data-testid="testimonial-section">
        <Reveal className="quote-inner">
          <Quote size={38} />
          <blockquote>“{t("quote.text")}”</blockquote>
          <span>{t("quote.author")}</span>
        </Reveal>
      </section>
      <section className="contact-banner section-wrap" data-testid="contact-banner">
        <div>
          <Reveal><span className="eyebrow">{t("banner.eyebrow")}</span></Reveal>
          <Reveal delay={0.08}><h2>{t("banner.t1")}<br /><em>{t("banner.t2")}</em></h2></Reveal>
        </div>
        <Reveal delay={0.15}>
          <button className="btn btn-gold" onClick={openContact} data-testid="footer-consultation-button">
            {t("banner.cta")} <ArrowUpRight size={17} />
          </button>
        </Reveal>
      </section>
    </>
  );
}
